import { useCallback, useMemo } from 'react';
import {
  IconButton,
  flattenErrorTypeArray,
  Flex,
  FlexColumn,
  ModelNewNumericInput,
  ModelSelect,
  SecondaryButton,
  Table,
  useDeleteAlert,
  ModelInput,
  InfoItemContainer,
  SVGInfo,
  Tooltip,
  TableColumn,
  ModelSelectSearchInput,
} from '@equifinance/shared';
import styled from 'styled-components';
import { TableStatusIcon } from '../../../Icons/TableStatusIcon';
import { useAccountContext } from '../../../../../contexts';
import { DeleteIcon, PlusIcon } from '../../../components/Icons';
import {
  Applicant,
} from '../../../../../contexts/account/Account';
import useErrorBasedRowStyles from '../../../../../hooks/useErrorBasedRowStyles';
import Section from '../../../components/Section';
import { ineSectionHeaderCss, ineSectionTitleCss, ineSectionBodyCss } from './IncomeAndEmploymentSection';
import { Frequency, OtherIncome, OtherIncomeType } from '../../../../../contexts/account/IncomeAndEmployment';

export const NonJobRelatedIncomeRefName = 'non-job-related-income';

const Info = styled.div`${({ theme }) => `
  border-radius: ${theme.borderRadius[2]};
  margin: ${theme.padding[3]} ${theme.padding[3]} 0 ${theme.padding[3]};
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.1);

  ${InfoItemContainer} {
    margin: 0;
  }
`}`;

const InfoContainer = styled(Flex)`${({ theme }) => `
  justify-content: space-between;
  min-height: ${theme.padding[6]};
  align-items: stretch;
  line-height ${theme.padding[6]};
`}`;

const SectionHeader = styled(FlexColumn)`${({ theme }) => `
  padding: 0 ${theme.padding[4]};
`}`;

const AddOtherIncomeContainer = styled(Flex)`${({ theme }) => `
  padding: ${theme.padding[4]} 0;
  justify-content: end;
`}`;

const AddOtherPlusIcon = styled(PlusIcon) <{ disabled?: boolean }>`${({ theme, disabled }) => `
  ${disabled && `{
    color: ${theme.colours.neutral[5]};
  }`};
`}`;

const IconContainer = styled.div`${({ theme }) => `
  width: ${theme.padding[5]};
  height: ${theme.padding[5]};
  margin: auto;
`}`;

const DeleteIconButton = styled(DeleteIcon)`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[3]};
`}`;

export default function NonJobIncomeSection({
  applicant,
  applicantIndex,
}: {
  applicant: Applicant,
  applicantIndex: number,
}) {
  const {
    account,
    appendToAccountArray,
    deleteFromAccountArray,
  } = useAccountContext();

  const { errors, warnings } = account ?? {};
  const { incomeAndEmployment } = applicant ?? {};
  const { otherIncomes } = incomeAndEmployment ?? {};
  const incomesModelPath = useMemo(() => `applicants[${applicantIndex}].incomeAndEmployment.otherIncomes`, [applicantIndex]);

  const addOtherIncome = () => {
    appendToAccountArray(incomesModelPath, { });
  };

  const conditionalRowStyles = useErrorBasedRowStyles(errors, warnings);

  const deleteAlert = useDeleteAlert();

  const deleteOtherIncome = (otherIncome: OtherIncome) => {
    const { id } = otherIncome;
    deleteAlert({
      onConfirm: () => deleteFromAccountArray(incomesModelPath, id),
      text: 'Are you sure you want to delete this record?',
    });
  };

  const otherIncomeIndices = useMemo(() => otherIncomes.reduce((prev, current, index) => ({
    ...prev,
    [current.id]: index,
  }), {} as Record<string, number>), [otherIncomes]);

  const sortedOtherIncomes = otherIncomes?.filter((income) => !!income?.id) ?? [];

  const DeleteOtherIncomeButton = useCallback((otherIncome: OtherIncome) => (
    <IconButton onClick={() => deleteOtherIncome(otherIncome)}>
      <DeleteIconButton />
    </IconButton>
  ), []);

  const Status = useCallback((otherIncome: OtherIncome) => (
    <IconContainer>
      <TableStatusIcon
        recordErrors={flattenErrorTypeArray(errors[otherIncome?.id]) ?? []}
        recordWarnings={flattenErrorTypeArray(warnings[otherIncome?.id]) ?? []}
        errors={flattenErrorTypeArray(errors?.[otherIncome?.id] ?? [])}
        showDefault={!otherIncome?.id}
      />
    </IconContainer>
  ), [errors]);

  const IncomeTypeSelect = useCallback((otherIncome: OtherIncome) => (
    <ModelSelectSearchInput
      isTable
      field={`${incomesModelPath}[${otherIncomeIndices[otherIncome.id]}].incomeType`}
      options={OtherIncomeType}
      // showValueTooltip="onHover
      required
      isTooltipError
    />
  ), [incomesModelPath, otherIncomeIndices]);

  const Amount = useCallback((otherIncome: OtherIncome) => (
    <ModelNewNumericInput
      field={`${incomesModelPath}[${otherIncomeIndices[otherIncome.id]}].amount`}
      prefix="£"
      isFixedDecimal
      required
      isTooltipError
      maxCharacters={10}
    />
  ), [errors, incomesModelPath, otherIncomeIndices]);

  const FrequencyTypeSelect = useCallback((otherIncome: OtherIncome) => (
    <ModelSelect
      field={`${incomesModelPath}[${otherIncomeIndices[otherIncome.id]}].frequency`}
      options={Frequency}
      required
      isTooltipError
    />
  ), [errors, incomesModelPath, otherIncomeIndices]);

  const Description = useCallback((otherIncome: OtherIncome) => {
    const index = otherIncomeIndices[otherIncome.id];
    const tooltipId = `error-tooltip-otherIncome-${index}`;
    return (
      <ModelInput
        id={`otherIncome-${index}`}
        field={`${incomesModelPath}[${index}].description`}
        required
        isTooltipError
      >
        {otherIncome.description && (
          <Tooltip
            id={tooltipId}
            place="top"
            html={otherIncome.description}
          />
        )}
      </ModelInput>
    );
  }, [errors, incomesModelPath]);

  const columns: TableColumn<OtherIncome>[] = [
    {
      name: 'Status',
      width: '54px',
      cell: (otherIncome) => Status(otherIncome),
    },
    {
      name: 'Income type',
      minWidth: '120px',
      maxWidth: '180px',
      cell: (otherIncome) => IncomeTypeSelect(otherIncome),
    },
    {
      name: 'Amount',
      minWidth: '120px',
      maxWidth: '170px',
      cell: (otherIncome) => Amount(otherIncome),
    },
    {
      name: 'Frequency',
      minWidth: '120px',
      maxWidth: '180px',
      cell: (otherIncome) => FrequencyTypeSelect(otherIncome),
    },
    {
      name: 'Description',
      cell: (otherIncome) => Description(otherIncome),
    },
    {
      name: '',
      width: '30px',
      cell: (otherIncome) => DeleteOtherIncomeButton(otherIncome),
    },
  ];

  return (
    <Section
      id={`${incomeAndEmployment?.id}`}
      title="Non-job related income"
      level={2}
      headerCss={ineSectionHeaderCss}
      titleCss={ineSectionTitleCss}
      bodyCss={ineSectionBodyCss}
      refName={NonJobRelatedIncomeRefName}
      titleIcon={<></>}
    >
      <Info>
        <SVGInfo>
          <InfoContainer>
            Additional income will need to be entered as Net income and adhere to our additional income guidelines.
          </InfoContainer>
        </SVGInfo>
      </Info>
      <SectionHeader>
        <AddOtherIncomeContainer>
          <SecondaryButton
            small
            onClick={addOtherIncome}
          >
            <AddOtherPlusIcon />
            Add other income
          </SecondaryButton>
        </AddOtherIncomeContainer>
      </SectionHeader>

      <Table
        columns={columns}
        data={sortedOtherIncomes}
        showSearch={false}
        conditionalRowStyles={conditionalRowStyles}
      />
    </Section>
  );
}
